import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../../theme';
import { useSupabase } from '../../../contexts/SupabaseContext';
import GuidedTour from '../../../components/tour/GuidedTour';
import TourButton from '../../../components/tour/TourButton';
import useTour from '../../../components/tour/useTour';
import { TOURS } from '../../../components/tour/tours';
import { useLiveGoldPrice } from '../../../utils/goldPrice';
import LiveGoldPriceCard from '../../../components/LiveGoldPriceCard';

// Colors for the pie chart
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28DF2',
  '#FF6B6B', '#4ECDC4', '#1A535C', '#FF9F1C', '#011627'
];

const DigitalGold = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const { goldPrice, goldPriceLoading, goldPriceError, refreshGoldPrice } = useLiveGoldPrice();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-gold-digital-gold', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openTransactionHistory, setOpenTransactionHistory] = useState(false);
  const [currentPlatform, setCurrentPlatform] = useState(null);
  const [currentPlatformTransactions, setCurrentPlatformTransactions] = useState([]);
  
  // State for transaction actions
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Edit form state
  const [editPrice, setEditPrice] = useState('');
  const [editAmountInvested, setEditAmountInvested] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState(new Date());
  const [editUnits, setEditUnits] = useState(0);
  
  // Form state
  const [platformName, setPlatformName] = useState(null);
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [price, setPrice] = useState('');
  const [amountInvested, setAmountInvested] = useState('');
  const [units, setUnits] = useState(0);
  
  // User's saved platform names (will be populated from existing investments)
  const [userPlatformNames, setUserPlatformNames] = useState([]);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};

  // Function to fetch investments from Supabase
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch investments');
      setLoading(false);
      return;
    }
    
    try {
    setLoading(true);
      
      console.log('Fetching digital gold investments for user ID:', user.id);
      const { data, error } = await supabase
        .from('digital_gold_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('purchase_date', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      console.log('Fetched digital gold data:', data);
      
      if (data && data.length > 0) {
        // Transform the data to match the expected format for the component
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          value: item.amount_invested,
          price: item.price,
          purchaseDate: item.purchase_date,
          units: item.units
        }));
        
        // Group investments by platform name
        const groupedInvestments = groupInvestmentsByPlatform(formattedData);
        
        setInvestments(groupedInvestments);
        const total = formattedData.reduce((sum, item) => sum + item.value, 0);
        setTotalValue(total);
        console.log('Total Digital Gold value:', total);
        
        // Extract unique platform names from user's investments
        const uniquePlatformNames = [...new Set(data.map(item => item.name))];
        setUserPlatformNames(uniquePlatformNames);
      } else {
        console.log('No digital gold investments found');
        setInvestments([]);
        setTotalValue(0);
        setUserPlatformNames([]);
      }
    } catch (error) {
      console.error('Error fetching investments:', error);
      // Fallback to empty data in case of error
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);
  
  // Group investments by platform name
  const groupInvestmentsByPlatform = (data) => {
    const groupedMap = data.reduce((acc, investment) => {
      if (!acc[investment.name]) {
        acc[investment.name] = {
          name: investment.name,
          totalValue: 0,
          totalUnits: 0,
          transactions: []
        };
      }
      
      acc[investment.name].totalValue += investment.value;
      acc[investment.name].totalUnits += parseFloat(investment.units || 0);
      acc[investment.name].transactions.push(investment);
      
      return acc;
    }, {});
    
    // Convert the grouped map to an array
    return Object.values(groupedMap).map((platform, index) => ({
      id: index + 1,
      name: platform.name,
      value: platform.totalValue,
      units: platform.totalUnits,
      transactions: platform.transactions
    }));
  };
  
  // Initial data fetch and connection test
  useEffect(() => {
    const init = async () => {
      try {
        // Test Supabase connection
        console.log('Testing Supabase connection...');
        const { data: connectionTest, error: connectionError } = await supabase
          .from('digital_gold_investments')
          .select('count')
          .limit(1);
          
        if (connectionError) {
          console.error('Supabase connection error:', connectionError);
        } else {
          console.log('Supabase connection successful:', connectionTest);
        }
        
        // Fetch investments
        await fetchInvestments();
      } catch (error) {
        console.error('Initialization error:', error);
        setLoading(false);
      }
    };
    
    init();
  }, [fetchInvestments]);

  // Calculate units when price or amount invested changes
  useEffect(() => {
    if (price && amountInvested && parseFloat(price) > 0 && parseFloat(amountInvested) > 0) {
      setUnits((parseFloat(amountInvested) / parseFloat(price)).toFixed(4));
    } else {
      setUnits('');
    }
  }, [price, amountInvested]);

  // Calculate units when price or amount invested changes for edit modal
  useEffect(() => {
    if (editPrice && editAmountInvested && parseFloat(editPrice) > 0 && parseFloat(editAmountInvested) > 0) {
      setEditUnits((parseFloat(editAmountInvested) / parseFloat(editPrice)).toFixed(4));
    } else {
      setEditUnits('');
    }
  }, [editPrice, editAmountInvested]);

  // Create pie chart data
  const pieChartData = investments.map((item) => ({
    id: item.id,
    value: item.value,
    label: item.name,
  }));

  // Current value of holdings at the live gold price (units are grams)
  const currentValueOf = (item) => (goldPrice ? parseFloat(item.units || 0) * goldPrice.pricePerGram : null);
  const totalCurrentValue = goldPrice
    ? investments.reduce((sum, item) => sum + currentValueOf(item), 0)
    : null;
  const totalGainPercent = totalCurrentValue !== null && totalValue > 0
    ? ((totalCurrentValue - totalValue) / totalValue) * 100
    : null;

  const handleOpenModal = () => {
    // Prefill today's live price for a new purchase; the user can still edit it
    if (!price && goldPrice) {
      setPrice(goldPrice.pricePerGram.toFixed(2));
    }
    setOpenModal(true);
  };
  const handleCloseModal = () => setOpenModal(false);

  const handleReset = () => {
    setPlatformName(null);
    setPurchaseDate(new Date());
    setPrice('');
    setUnits('');
    setAmountInvested('');
  };

  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }
    
    if (!platformName || !purchaseDate || !price || !amountInvested) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Get the platform name (either from string input or from selected option)
      const platformNameValue = typeof platformName === 'string' ? platformName : platformName.label || platformName;
      
      // Calculate the units based on amount and price
      const calculatedUnits = parseFloat(amountInvested) / parseFloat(price);
      
      // Prepare the data for Supabase - only include fields that exist in the schema
      const newInvestment = {
        name: platformNameValue,
        purchase_date: purchaseDate.toISOString().split('T')[0],
        price: parseFloat(price),
        amount_invested: parseFloat(amountInvested),
        units: calculatedUnits,
        user_id: user.id // Make sure to include user_id
      };

      console.log('Creating new investment:', newInvestment);

      // Insert data into Supabase
      const { data, error } = await supabase
        .from('digital_gold_investments')
        .insert([newInvestment])
        .select();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Insert response:', data);

      // Add the new investment to the list and update total value
      if (data && data.length > 0) {
        // Refresh the data from Supabase
        await fetchInvestments();
      }

      // Reset form and close modal
      handleReset();
      handleCloseModal();
      
    } catch (error) {
      console.error('Error saving investment:', error);
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTransactionHistory = (platform) => {
    setCurrentPlatform(platform);
    setCurrentPlatformTransactions(platform.transactions || []);
    setOpenTransactionHistory(true);
  };
  
  const handleCloseTransactionHistory = () => {
    setOpenTransactionHistory(false);
    setCurrentPlatform(null);
    setCurrentPlatformTransactions([]);
  };

  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditModalOpen = () => {
    handleTransactionMenuClose();
    
    if (!currentTransaction) {
      console.error('No transaction selected for editing');
      return;
    }
    
    console.log('Editing transaction:', currentTransaction);
    
    // Set form values with defaults in case of null/undefined values
    setEditPrice(currentTransaction.price ? currentTransaction.price.toString() : '');
    setEditAmountInvested(currentTransaction.value ? currentTransaction.value.toString() : '');
    setEditPurchaseDate(currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate) : new Date());
    // Units will be calculated automatically based on price and amount invested
  
    setOpenEditModal(true);
  };

  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };

  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleUpdateTransaction = async () => {
    if (!editPrice || !editAmountInvested || !editPurchaseDate) {
      alert('Please fill all required fields');
      return;
    }

    if (!currentTransaction || !currentTransaction.id) {
      console.error('Missing transaction ID for update');
      alert('Cannot update transaction: Missing ID');
      return;
    }

    try {
      setLoading(true);
      
      // Calculate the units based on amount and price
      const calculatedUnits = parseFloat(editAmountInvested) / parseFloat(editPrice);
      
      // Prepare the data for Supabase - ensure proper data types
      const updatedTransaction = {
        name: currentTransaction.name,
        purchase_date: editPurchaseDate instanceof Date 
          ? editPurchaseDate.toISOString().split('T')[0] 
          : new Date(editPurchaseDate).toISOString().split('T')[0],
        price: parseFloat(editPrice),
        amount_invested: parseFloat(editAmountInvested),
        units: calculatedUnits,
      };

      console.log('Updating transaction:', currentTransaction.id, updatedTransaction);

      // Update data in Supabase
      const { data, error } = await supabase
        .from('digital_gold_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update response:', data);

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (currentPlatform && openTransactionHistory) {
        // Get the latest transactions for this platform
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('digital_gold_investments')
          .select('*')
          .eq('name', currentPlatform.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError && refreshedTransactions) {
          const formattedTransactions = refreshedTransactions.map(item => ({
            id: item.id,
            name: item.name,
            value: item.amount_invested,
            price: item.price,
            purchaseDate: item.purchase_date,
            units: item.units
          }));
          
          setCurrentPlatformTransactions(formattedTransactions);
        }
      }

      // Close the edit modal
      handleEditModalClose();
      
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      
      console.log('Deleting transaction:', currentTransaction.id);
      
      // Delete from Supabase
      const { error } = await supabase
        .from('digital_gold_investments')
        .delete()
        .eq('id', currentTransaction.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (currentPlatform && openTransactionHistory) {
        // Get the latest transactions for this platform
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('digital_gold_investments')
          .select('*')
          .eq('name', currentPlatform.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            const formattedTransactions = refreshedTransactions.map(item => ({
              id: item.id,
              name: item.name,
              value: item.amount_invested,
              price: item.price,
              purchaseDate: item.purchase_date,
              units: item.units
            }));
            
            setCurrentPlatformTransactions(formattedTransactions);
          } else {
            // No transactions left for this platform, close the modal
            handleCloseTransactionHistory();
          }
        }
      }

      // Close the delete dialog
      handleDeleteDialogClose();
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedInvestments = [...investments].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (key === 'name') {
      return direction === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    }
    if (key === 'value') {
      return direction === 'asc' ? a.value - b.value : b.value - a.value;
    }
    if (key === 'allocation') {
      const pctA = totalValue > 0 ? (a.value / totalValue) * 100 : 0;
      const pctB = totalValue > 0 ? (b.value / totalValue) * 100 : 0;
      return direction === 'asc' ? pctA - pctB : pctB - pctA;
    }
    return 0;
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={() => navigate('/investments/gold')}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              bgcolor: 'action.hover',
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box data-tour="inv-prod-summary">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Digital Gold
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Invested: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(totalValue)}
            {totalCurrentValue !== null && totalGainPercent !== null && (
              <>
                {' · Current Value: '}{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(totalCurrentValue)}
                <Box component="span" sx={{ ml: 1, fontWeight: 600, color: totalGainPercent >= 0 ? 'success.main' : 'error.main' }}>
                  ({totalGainPercent >= 0 ? '+' : ''}{totalGainPercent.toFixed(2)}%)
                </Box>
              </>
            )}
          </Typography>
        </Box>
      </Box>
        
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <TourButton onClick={tour.start} />
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={() => { fetchInvestments(); refreshGoldPrice(); }} 
              sx={{ 
                color: 'primary.main',
                '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
              }}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button 
            data-tour="inv-prod-add"
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 2,
            }}
          >
            Add Investment
          </Button>
        </Box>
      </Box>

      <Box data-tour="inv-gold-price">
        <LiveGoldPriceCard goldPrice={goldPrice} loading={goldPriceLoading} error={goldPriceError} />
      </Box>

      {/* Modal for adding new investment */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-investment-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Add New Digital Gold Investment
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Autocomplete
                value={platformName}
                onChange={(event, newValue) => setPlatformName(newValue)}
                freeSolo
                autoHighlight
                options={userPlatformNames}
                getOptionLabel={(option) => {
                  // Handle both string values and objects
                  return typeof option === 'string' ? option : option;
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter(option => 
                    option.toLowerCase().includes(params.inputValue.toLowerCase())
                  );
                  
                  // Add the user's input as an option if it's not already in the list
                  if (params.inputValue !== '' && !filtered.includes(params.inputValue)) {
                    filtered.push(params.inputValue);
                  }
                  
                  return filtered;
                }}
                groupBy={(option) => {
                  // Custom entered value
                  if (!userPlatformNames.includes(option)) {
                    return "Custom Entry";
                  }
                  // User's existing platforms
                  else {
                    return "Your Platforms";
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Platform Name" 
                    required 
                    fullWidth
                    placeholder="Enter or select platform name"
                    helperText="You can type a new platform name or select from your existing platforms"
                  />
                )}
              />
              
              <DatePicker
                label="Date of Purchase"
                value={purchaseDate}
                onChange={(newValue) => setPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="Gold Price (per gram)"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Amount Invested"
                value={amountInvested}
                onChange={(e) => setAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Units (in grams)"
                value={units}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                helperText="Auto-calculated (Amount Invested ÷ Price)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleReset}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Reset
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Save Investment
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Transaction Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleTransactionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 150, borderRadius: 2 }
        }}
      >
        <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
          <EditIcon fontSize="small" color="primary" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
          <DeleteIcon fontSize="small" color="error" />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Transaction Modal */}
      <Modal
        open={openEditModal}
        onClose={handleEditModalClose}
        aria-labelledby="edit-transaction-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Edit Transaction
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Typography variant="subtitle1">
                Platform: {currentTransaction?.name}
              </Typography>
              
              <DatePicker
                label="Date of Purchase"
                value={editPurchaseDate}
                onChange={(newValue) => setEditPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="Gold Price (per gram)"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Amount Invested"
                value={editAmountInvested}
                onChange={(e) => setEditAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Units (in grams)"
                value={editUnits}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                helperText="Auto-calculated (Amount Invested ÷ Price)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateTransaction}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Update Transaction
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-transaction-dialog"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </DialogContentText>
          {currentTransaction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Transaction Details:
              </Typography>
              <Typography variant="body2">
                Platform: {currentTransaction.name}<br />
                Date: {currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate).toLocaleDateString('en-IN') : '-'}<br />
                Amount: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(currentTransaction.value)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteDialogClose} 
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTransaction} 
            variant="contained" 
            color="error"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : investments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 4,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">No Digital Gold Found</Typography>
          <Typography variant="body2">
            No digital gold investments found. Start investing to see them here.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
          >
            Add Your First Investment
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Investments Table */}
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-prod-holdings"
              sx={{
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Fund Details
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                        onClick={() => handleSort('name')}
                      >
                        Platform Name
                        {sortConfig.key === 'name' && (
                          <span style={{ marginLeft: 8 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                        onClick={() => handleSort('value')}
                      >
                        Invested
                        {sortConfig.key === 'value' && (
                          <span style={{ marginLeft: 8 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>
                        Current Value
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                        onClick={() => handleSort('allocation')}
                      >
                        Allocation
                        {sortConfig.key === 'allocation' && (
                          <span style={{ marginLeft: 8 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedInvestments.map((investment, index) => (
                      <TableRow
                        key={investment.id}
                        onClick={() => handleOpenTransactionHistory(investment)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: COLORS[index % COLORS.length],
                                mr: 2,
                              }}
                            />
                            {investment.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(investment.value)}
                        </TableCell>
                        <TableCell align="right">
                          {currentValueOf(investment) !== null ? new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(currentValueOf(investment)) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {((investment.value / totalValue) * 100).toFixed(1)}%
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View transactions">
                            <IconButton size="small" sx={{ color: 'primary.main' }}>
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {totalCurrentValue !== null ? new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(totalCurrentValue) : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          
          {/* Pie Chart */}
          <Grid item xs={12} md={4} data-tour="inv-prod-charts">
            <Paper
              sx={{
                height: '100%',
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Distribution
                </Typography>
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                }}
              >
                {pieChartData.length > 0 && (
                  <PieChart colors={chartPalette}
                    series={[
                      {
                        data: pieChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                      },
                    ]}
                    height={450}
                    legend={{
                      hidden: true
                    }}
                    margin={{ top: 10, bottom: 50, left: 20, right: 20 }}
                    slotProps={{
                      legend: {
                        direction: 'row',
                        position: { vertical: 'bottom', horizontal: 'middle' },
                        padding: { top: 10, bottom: 0, left: 0, right: 0 },
                        itemMarkWidth: 8,
                        itemMarkHeight: 8,
                        markGap: 5,
                        itemGap: 15,
                        sx: {
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          gap: 2,
                        },
                      },
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* Transaction History Modal */}
      <Modal
        open={openTransactionHistory}
        onClose={handleCloseTransactionHistory}
        aria-labelledby="transaction-history-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: 700 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                {currentPlatform?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Value: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(currentPlatform?.value || 0)} | 
                Total Units: {(currentPlatform?.units || 0).toFixed(4)}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseTransactionHistory} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Chip 
              icon={<ReceiptIcon fontSize="small" />} 
              label="Transaction History" 
              color="primary" 
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Date</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Price (₹)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Amount (₹)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Units</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPlatformTransactions.length > 0 ? (
                  currentPlatformTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {transaction.purchaseDate 
                          ? new Date(transaction.purchaseDate).toLocaleDateString('en-IN')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.price 
                          ? transaction.price.toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(transaction.value)}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.units 
                          ? parseFloat(transaction.units).toFixed(4)
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      No transaction history available
                    </TableCell>
                  </TableRow>
                )}
                {currentPlatformTransactions.length > 0 && (
                  <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell align="right" />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(currentPlatformTransactions.reduce((sum, tx) => sum + tx.value, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {currentPlatformTransactions
                        .reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0)
                        .toFixed(4)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleCloseTransactionHistory}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      <GuidedTour steps={TOURS['inv-gold-digital-gold']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default DigitalGold;